import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CATEGORIES, CATEGORY_LABEL, STAGES, STAGE_LABEL, get } from "../api";
import { useAuth } from "../auth";
import type { CustomerRow, Stage } from "../api";
import { CategoryChip, StagePill } from "../Classification";
import { writeCover } from "../customerFilter";
import { CoverFilter, UNASSIGNED, useCsms } from "../CoverFilter";

const SENTIMENT_CLASS: Record<string, string> = {
  positive: "pos",
  negative: "neg",
  mixed: "mix",
  neutral: "neu",
};

/** The category/stage filter value for customers nobody has classified. */
const UNSET = "unset";

export default function Customers() {
  const { me } = useAuth();
  const isAdmin = me?.user.is_admin ?? false;
  const [params, setParams] = useSearchParams();
  // A uuid, `none`, or absent. It lives in the URL so a filtered view is a
  // link somebody can send, and the back button undoes a selection.
  const filter = params.get("user");

  const [rows, setRows] = useState<CustomerRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const csms = useCsms(isAdmin);

  useEffect(() => {
    // Cleared first: leaving the old tiles under a new label would show
    // one user's customers attributed to another for as long as the
    // request takes.
    setRows(null);
    setError(null);
    void get<CustomerRow[]>(`/api/customers${filter ? `?user=${encodeURIComponent(filter)}` : ""}`)
      .then(setRows)
      .catch((e) => setError(e.message));
  }, [filter]);

  // Remembered so a customer's sidebar lists what this page listed.
  useEffect(() => {
    if (!filter) return writeCover(null);
    const label =
      filter === UNASSIGNED
        ? "Nobody covers"
        : `Covered by ${csms.find((u) => u.id === filter)?.name ?? "a CSM"}`;
    writeCover({ user: filter, label });
  }, [filter, csms]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const select = (value: string) => setParam("user", value);

  // Name, category and stage narrow in the browser: the scoped list is
  // already here in full, so filtering it adds no query and no second
  // path to authorize.
  const q = params.get("q") ?? "";
  const category = params.get("category") ?? "";
  const stage = params.get("stage") ?? "";
  const narrowed = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows ?? []).filter(
      (c) =>
        (!needle ||
          c.display_name.toLowerCase().includes(needle) ||
          c.name.includes(needle)) &&
        matches(c.category, category),
    );
  }, [rows, q, category]);
  // Stage counts are taken before the stage filter, so each pill says how
  // many it would show -- the bar doubles as the pipeline at a glance.
  const shown = useMemo(() => narrowed.filter((c) => matches(c.stage, stage)), [narrowed, stage]);
  const clearFilters = () => {
    const next = new URLSearchParams(params);
    ["q", "category", "stage"].forEach((k) => next.delete(k));
    setParams(next, { replace: true });
  };
  const filtering = Boolean(q || category || stage);

  if (error) return <div className="wrap"><div className="error">{error}</div></div>;

  return (
    <div className="wrap">
      <h1>Customers</h1>

      {isAdmin && <CoverFilter value={filter ?? ""} csms={csms} onChange={select} />}

      {!rows ? (
        <div className="empty">Loading…</div>
      ) : rows.length === 0 ? (
        <Nothing filter={filter} isAdmin={isAdmin} onClear={() => select("")} />
      ) : (
        <>
          <Filters
            q={q}
            category={category}
            stage={stage}
            rows={narrowed}
            filtering={filtering}
            onChange={setParam}
            onClear={clearFilters}
          />

          <p className="sub">
            {filtering && `${shown.length} of `}
            {rows.length} customer{rows.length === 1 ? "" : "s"}
            {filter === UNASSIGNED
              ? " nobody covers."
              : filter
                ? ` covered by ${csms.find((u) => u.id === filter)?.name ?? "them"}.`
                : " in your scope."}
          </p>

          {shown.length === 0 ? (
            <div className="empty">
              No customers match.{" "}
              <button className="btn tiny" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid">
              {shown.map((c) => <Tile key={c.id} c={c} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// --- The filters --------------------------------------------------------

/** An empty filter matches everything; `unset` matches only null. */
function matches(value: string | null, filter: string): boolean {
  if (!filter) return true;
  if (filter === UNSET) return value === null;
  return value === filter;
}

/** Name search, category, and the stage bar. Open to everybody: they only
    narrow a list the caller was already sent. */
function Filters({
  q,
  category,
  stage,
  rows,
  filtering,
  onChange,
  onClear,
}: {
  q: string;
  category: string;
  stage: string;
  rows: CustomerRow[];
  filtering: boolean;
  onChange: (key: string, value: string) => void;
  onClear: () => void;
}) {
  const count = (s: string) => rows.filter((c) => matches(c.stage, s)).length;
  const unset = count(UNSET);
  return (
    <>
      <div className="filters">
        <input
          type="search"
          placeholder="Search by name…"
          aria-label="Search by name"
          value={q}
          onChange={(e) => onChange("q", e.target.value)}
        />
        <select
          aria-label="Category"
          value={category}
          onChange={(e) => onChange("category", e.target.value)}
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABEL[c]}
            </option>
          ))}
          <option value={UNSET}>Unset</option>
        </select>
        {filtering && (
          <button className="btn tiny" onClick={onClear}>
            Clear
          </button>
        )}
      </div>

      <div className="stagebar" role="group" aria-label="Stage">
        <button
          className={`pill stg-all${stage ? "" : " on"}`}
          aria-pressed={!stage}
          onClick={() => onChange("stage", "")}
        >
          All<span className="n">{rows.length}</span>
        </button>
        {STAGES.map((s) => (
          <StageToggle key={s} stage={s} n={count(s)} on={stage === s} onChange={onChange} />
        ))}
        {/* Offered only while something is unclassified: it is the "did we
            forget anyone" question, and a zero answers itself. */}
        {(unset > 0 || stage === UNSET) && (
          <StageToggle stage={UNSET} n={unset} on={stage === UNSET} onChange={onChange} />
        )}
      </div>
    </>
  );
}

function StageToggle({
  stage,
  n,
  on,
  onChange,
}: {
  stage: Stage | typeof UNSET;
  n: number;
  on: boolean;
  onChange: (key: string, value: string) => void;
}) {
  return (
    <button
      className={`pill stg-${stage}${on ? " on" : ""}`}
      aria-pressed={on}
      onClick={() => onChange("stage", on ? "" : stage)}
    >
      {stage === UNSET ? "Unset" : STAGE_LABEL[stage]}
      <span className="n">{n}</span>
    </button>
  );
}

// --- Empty --------------------------------------------------------------

/** Three different nothings, which mean three different things. */
function Nothing({
  filter,
  isAdmin,
  onClear,
}: {
  filter: string | null;
  isAdmin: boolean;
  onClear: () => void;
}) {
  if (filter === UNASSIGNED) {
    return <div className="empty">Every customer is covered by somebody.</div>;
  }
  if (filter) {
    return (
      <div className="empty">
        No customers are assigned to them yet.{" "}
        <button className="btn tiny" onClick={onClear}>
          Show all
        </button>
      </div>
    );
  }
  return (
    <div className="empty">
      {isAdmin
        ? "No customers yet. Add one from the admin section."
        : "No customers are assigned to you yet. An admin can add you to one."}
    </div>
  );
}

// --- A tile -------------------------------------------------------------

function Tile({ c }: { c: CustomerRow }) {
  const r = c.latest_report;
  return (
    <Link to={`/c/${c.name}`} className="card">
      <h3>{c.display_name}</h3>
      <div className="tags">
        <StagePill stage={c.stage} />
        <CategoryChip category={c.category} />
      </div>
      <div className="meta" style={{ marginTop: 6 }}>
        {r ? (
          <>
            {r.report_date} · {r.window_days}-day window · {r.mode}
          </>
        ) : (
          "no report yet"
        )}
      </div>

      {r?.sentiment && (
        <div style={{ marginTop: 10 }}>
          <span className={`badge ${SENTIMENT_CLASS[r.sentiment] ?? "neu"}`}>{r.sentiment}</span>
        </div>
      )}

      {/* An uncovered gap looks exactly like a quiet week unless it
          is said out loud -- invariant 19. */}
      {r && r.uncovered_days > 0 && (
        <div className="banner">
          {r.uncovered_days} day{r.uncovered_days === 1 ? "" : "s"} before this window were never
          covered by any report.
        </div>
      )}

      {r?.executive_summary && <p>{r.executive_summary}</p>}

      {r && (
        <div className="stats">
          <span className="chip">{r.highlights} highlights</span>
          <span className="chip">{r.lowlights} lowlights</span>
          <span className="chip">{r.open_actions} open</span>
        </div>
      )}
    </Link>
  );
}
